import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Badge, Input, Select, Modal, Table } from '../components/Common';
import RoleReference, { ROLE_SECTIONS } from '../components/RoleReference';
import PasswordPolicyHelp, { DEFAULT_PASSWORD_RULES, passwordFailures } from '../components/PasswordPolicyHelp';

// Mirrors CLIENT_ASSIGNABLE_GROUPS in backend/routes/users.js — the API enforces it.
const CLIENT_GROUP_NAMES = ['Client Admins', 'Penetration Testers', 'Client Viewers', 'Report Generators',
  'Engagement Admins', 'Technical Managers', 'Report Admins', 'Client Billing Admins', 'AI Pentest'];

/** Last logon as a short relative label; exact timestamp goes in the title attribute. */
function formatLastLogin(value) {
  if (!value) return 'Never';
  const then = new Date(value);
  if (isNaN(then.getTime())) return 'Never';
  const mins = Math.floor((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return then.toLocaleDateString();
}

// Flat lookup of documented groups, for the tooltips on the group checkboxes.
const ROLE_SUMMARIES = ROLE_SECTIONS.reduce((acc, section) => {
  section.roles.forEach(r => { acc[r.name] = r.summary; });
  return acc;
}, {});

export default function UserManagement() {
  const { apiFetch, user, hasGroup } = useAuth();
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [passwordRules, setPasswordRules] = useState(DEFAULT_PASSWORD_RULES);
  // '' = all, '__msp__' = MSP-side accounts (no client), otherwise a client id
  const [clientFilter, setClientFilter] = useState('');
  const [authFilter, setAuthFilter] = useState(''); // '' = all, 'local' = local, 'oidc' = SSO
  const [refsModal, setRefsModal] = useState(null);
  const [deactivating, setDeactivating] = useState(false);
  const [seatWarning, setSeatWarning] = useState(null);
  const [seatInfo, setSeatInfo] = useState(null);

  const isSuperAdmin = hasGroup('Super Admins');
  const isClientAdmin = user?.groups?.includes('Client Admins') && !user?.groups?.includes('Super Admins');

  const load = useCallback(async () => {
    const [uRes, gRes, cRes] = await Promise.all([
      apiFetch('/users'), apiFetch('/groups'),
      (user?.isMsp || user?.groups?.includes('Super Admins')) ? apiFetch('/clients') : Promise.resolve({ json: () => [] })
    ]);
    setUsers(await uRes.json());
    setGroups(await gRes.json());
    setClients(await cRes.json());
    // Rule wording comes from the API so the help text matches what it enforces
    try {
      const pRes = await apiFetch('/users/password-policy');
      if (pRes.ok) {
        const policy = await pRes.json();
        if (policy.rules?.length) setPasswordRules(policy.rules);
      }
    } catch (err) { /* keep the built-in rule list */ }
  }, [apiFetch, user]);

  useEffect(() => { load(); }, [load]);

  const checkSeats = useCallback(async (clientId) => {
    if (!clientId) { setSeatWarning(null); setSeatInfo(null); return; }
    try {
      const res = await apiFetch(`/licenses/check/${clientId}`);
      if (res.ok) {
        const data = await res.json();
        if (data.seats) setSeatInfo(data.seats);
        else setSeatInfo(null);
        if (!data.allowed) {
          setSeatWarning(data.reason || 'License seat limit reached. Contact sales for more seats.');
        } else if (data.seats) {
          const remaining = data.seats.total - data.seats.used;
          setSeatWarning(remaining <= 2 ? `${data.seats.used} of ${data.seats.total} seats used (${remaining} remaining)` : null);
        } else {
          setSeatWarning(null);
        }
      }
    } catch { setSeatWarning(null); setSeatInfo(null); }
  }, [apiFetch]);

  useEffect(() => {
    if (isClientAdmin && user?.clientId) checkSeats(user.clientId);
  }, [isClientAdmin, user, checkSeats]);

  useEffect(() => {
    if (isSuperAdmin && clientFilter && clientFilter !== '__msp__') checkSeats(clientFilter);
    else if (isSuperAdmin) setSeatInfo(null);
  }, [clientFilter, isSuperAdmin, checkSeats]);

  const save = async () => {
    setFormError('');
    if (!editing) {
      const failures = passwordFailures(form.password, passwordRules);
      if (failures.length > 0) {
        setFormError('Password does not meet the requirements: ' + failures.map(f => f.label).join('; '));
        return;
      }
    }
    const body = {
      username: form.username, email: form.email, password: form.password,
      fullName: form.fullName, role: 'viewer',
      clientId: isClientAdmin ? user.clientId : form.clientId || null,
      isMsp: isClientAdmin ? false : form.isMsp === 'true',
      isActive: form.isActive !== 'false',
      groupIds: form.groupIds || []
    };
    let res;
    if (editing) {
      delete body.username; delete body.password;
      // A Client Admin cannot move a user between clients, and the API ignores
      // the field for them. Sending it echoed the client id from this session,
      // which is wrong if their own client changed after they signed in.
      if (isClientAdmin) delete body.clientId;
      res = await apiFetch(`/users/${editing.id}`, { method: 'PUT', body: JSON.stringify(body) });
    } else {
      res = await apiFetch('/users', { method: 'POST', body: JSON.stringify(body) });
    }
    if (res && !res.ok) {
      const data = await res.json().catch(() => ({}));
      setFormError(data.error || 'Failed to save user');
      return;
    }
    setShowModal(false); setForm({}); setEditing(null); load();
  };

  // A user with history cannot be deleted (foreign keys). Rather than an
  // opaque "has associated records", show exactly what references them.
  const deleteUser = async (row) => {
    const label = row.full_name || row.username;
    if (!window.confirm(`Delete user "${label}"? This cannot be undone.`)) return;
    try {
      const res = await apiFetch(`/users/${row.id}`, { method: 'DELETE' });
      if (res.ok) { load(); return; }

      const err = await res.json().catch(() => ({}));
      if (err.code === 'HAS_REFERENCES') {
        setRefsModal({ user: row, message: err.error, data: err.references || null, loading: !err.references });
        // Defensive: if the delete response could not resolve them, fetch directly.
        if (!err.references) await loadReferences(row);
      } else {
        alert(err.error || 'Failed to delete user');
      }
    } catch {
      alert('Failed to delete user');
    }
  };

  const loadReferences = async (row) => {
    try {
      const res = await apiFetch(`/users/${row.id}/references`);
      const data = res.ok ? await res.json() : null;
      setRefsModal(m => (m ? { ...m, data, loading: false } : m));
    } catch {
      setRefsModal(m => (m ? { ...m, loading: false } : m));
    }
  };

  const deactivateUser = async (row) => {
    setDeactivating(true);
    try {
      const res = await apiFetch(`/users/${row.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          email: row.email, fullName: row.full_name, role: 'viewer',
          clientId: row.client_id, isMsp: !!row.is_msp, isActive: false,
          groupIds: groups.filter(g => row.groups?.includes(g.name)).map(g => g.id),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to deactivate user');
        return;
      }
      setRefsModal(null);
      load();
    } catch {
      alert('Failed to deactivate user');
    } finally {
      setDeactivating(false);
    }
  };

  const handleResetPassword = async () => {
    setResetError(''); setResetSuccess('');
    if (!resetPassword || !resetConfirm) {
      setResetError('Both fields are required'); return;
    }
    const failures = passwordFailures(resetPassword, passwordRules);
    if (failures.length > 0) {
      setResetError('Password does not meet the requirements: ' + failures.map(f => f.label).join('; '));
      return;
    }
    if (resetPassword !== resetConfirm) {
      setResetError('Passwords do not match'); return;
    }
    setResetLoading(true);
    try {
      const res = await apiFetch(`/users/${resetUser.id}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ newPassword: resetPassword })
      });
      if (!res.ok) {
        const data = await res.json();
        setResetError(data.error || 'Failed to reset password');
      } else {
        setResetSuccess(`Password reset successfully for ${resetUser.username}`);
        setResetPassword(''); setResetConfirm('');
        setTimeout(() => { setShowResetModal(false); setResetSuccess(''); setResetUser(null); }, 1500);
      }
    } catch (err) {
      setResetError('Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  // Filter groups visible to the current admin.
  //
  // AI Pentest is additionally withheld when the user being edited belongs to a
  // client that is not AI-enabled: the group would be inert there, since the
  // API refuses the agent for such a client whether or not the group is held.
  // An MSP account (no client) is not subject to the flag.
  const targetClientId = isClientAdmin ? user?.clientId : form.clientId;
  const targetClient = clients.find(c => c.id === targetClientId);
  const aiAllowedForTarget = !targetClientId || targetClient?.ai_enabled === true;

  const availableGroups = groups.filter(g => {
    if (g.name === 'AI Pentest' && !aiAllowedForTarget) return false;
    if (isClientAdmin) return CLIENT_GROUP_NAMES.includes(g.name);
    return true;
  });

  // Any group in the database that the role reference doesn't document, so custom
  // groups still show up in the help panel with their saved description.
  //
  // Drawn from availableGroups, not the raw list: /api/groups is deliberately
  // unfiltered (see the comment on that route -- filtering it would make a user
  // edit silently strip memberships it could not see), so building the help
  // panel from it put MSP-side groups a client admin can neither assign nor
  // hold -- Recon Operators among them -- into their documentation.
  const undocumentedGroups = availableGroups.filter(g => !ROLE_SUMMARIES[g.name]);

  // Client filter (Super Admins only — they are the ones who see every tenant).
  // Options come from the client list, with any client that appears on a user
  // folded in so a filter is never missing an option the table can show.
  const clientOptions = (() => {
    const byId = new Map();
    clients.forEach(c => byId.set(c.id, c.name));
    users.forEach(u => { if (u.client_id && !byId.has(u.client_id)) byId.set(u.client_id, u.client_name || 'Unknown client'); });
    return [...byId.entries()]
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  })();

  const visibleUsers = users.filter(u => {
    if (clientFilter === '__msp__' && u.client_id) return false;
    if (clientFilter && clientFilter !== '__msp__' && u.client_id !== clientFilter) return false;
    if (authFilter === 'local' && u.sso_provider_id) return false;
    if (authFilter === 'oidc' && !u.sso_provider_id) return false;
    return true;
  });

  const columns = [
    { key: 'username', label: 'Username' },
    { key: 'full_name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'groups', label: 'Groups', render: (v) => (
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {(v || []).filter(Boolean).map(g => <Badge key={g}>{g}</Badge>)}
      </div>
    )},
    { key: 'client_name', label: 'Client', render: v => v || 'MSP' },
    { key: 'last_login', label: 'Last Logon', render: v => (
      <span title={v ? new Date(v).toLocaleString() : 'This account has never signed in'}
        style={{ fontSize: 13, color: v ? 'var(--text)' : 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
        {formatLastLogin(v)}
      </span>
    )},
    { key: 'is_active', label: 'Active', render: v => v ? '✅' : '❌' },
    { key: 'id', label: 'Actions', render: (v, row) => (
      <div style={{ display: 'flex', gap: 6 }}>
        <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
          onClick={(e) => {
            e.stopPropagation();
            setEditing(row);
            setFormError('');
            setForm({
              email: row.email, fullName: row.full_name,
              clientId: row.client_id, isMsp: String(row.is_msp), isActive: String(row.is_active),
              groupIds: groups.filter(g => row.groups?.includes(g.name)).map(g => g.id)
            });
            setShowModal(true);
          }}>Edit</Button>
        <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
          onClick={(e) => {
            e.stopPropagation();
            setResetUser(row);
            setResetPassword(''); setResetConfirm('');
            setResetError(''); setResetSuccess('');
            setShowResetModal(true);
          }}>🔑 Reset Password</Button>
        {isSuperAdmin && row.id !== user.id && (
          <Button variant="danger" style={{ padding: '4px 12px', fontSize: 12 }}
            onClick={(e) => { e.stopPropagation(); deleteUser(row); }}>🗑️ Delete</Button>
        )}
      </div>
    )}
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700 }}>User Management</h1>
          {seatInfo && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px',
              borderRadius: 12, fontSize: 12, fontWeight: 600,
              background: seatInfo.used >= seatInfo.total ? 'rgba(239,68,68,0.1)' : seatInfo.total - seatInfo.used <= 2 ? 'rgba(245,158,66,0.1)' : 'rgba(52,211,153,0.1)',
              color: seatInfo.used >= seatInfo.total ? '#ef4444' : seatInfo.total - seatInfo.used <= 2 ? '#f59e42' : '#34d399',
              border: `1px solid ${seatInfo.used >= seatInfo.total ? 'rgba(239,68,68,0.2)' : seatInfo.total - seatInfo.used <= 2 ? 'rgba(245,158,66,0.2)' : 'rgba(52,211,153,0.2)'}`,
            }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
              {seatInfo.used} / {seatInfo.total} seats
            </span>
          )}
        </div>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <Button onClick={() => { setEditing(null); setForm(isClientAdmin ? { clientId: user?.clientId, isMsp: 'false' } : {}); setFormError(''); setSeatWarning(null); setShowModal(true); if (isClientAdmin && user?.clientId) checkSeats(user.clientId); }}>+ New User</Button>
          {seatWarning && seatWarning.includes('limit reached') && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, marginTop: 6, padding: '8px 12px',
              background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: 8, fontSize: 12, color: '#ef4444', whiteSpace: 'nowrap', zIndex: 10,
              pointerEvents: 'none',
            }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ verticalAlign: 'middle', marginRight: 4 }}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              {seatWarning}
            </div>
          )}
        </div>
      </div>
      <RoleReference extraGroups={undocumentedGroups} />
      {isSuperAdmin && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap' }}>
          <Select label="Client" style={{ marginBottom: 0, minWidth: 260 }}
            value={clientFilter} onChange={e => setClientFilter(e.target.value)}
            options={[
              { value: '', label: `All clients (${users.length})` },
              { value: '__msp__', label: 'MSP (no client)' },
              ...clientOptions,
            ]} />
          <Select label="Auth Type" style={{ marginBottom: 0, minWidth: 160 }}
            value={authFilter} onChange={e => setAuthFilter(e.target.value)}
            options={[
              { value: '', label: 'All accounts' },
              { value: 'local', label: 'Local' },
              { value: 'oidc', label: 'OIDC / SSO' },
            ]} />
          {(clientFilter || authFilter) && (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', paddingBottom: 10 }}>
              Showing {visibleUsers.length} of {users.length} users
            </div>
          )}
        </div>
      )}
      <Table columns={columns} data={visibleUsers} />
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit User' : 'New User'} width={700} noOverlayClose>
        {formError && (
          <div style={{ padding: '10px 14px', borderRadius: 8, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', marginBottom: 16, fontSize: 14 }}>
            {formError}
          </div>
        )}
        {!editing && <Input label="Username *" value={form.username || ''} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} />}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Input label="Full Name" value={form.fullName || ''} onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))} />
          <Input label="Email *" value={form.email || ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
        </div>
        {!editing && (
          <>
            <Input label="Password *" type="password" value={form.password || ''}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
            <PasswordPolicyHelp password={form.password || ''} rules={passwordRules} />
          </>
        )}
        {isSuperAdmin && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Select label="MSP User" options={[{ value: 'false', label: 'No (Client)' }, { value: 'true', label: 'Yes (MSP)' }]}
              value={form.isMsp || 'false'} onChange={e => setForm(f => ({ ...f, isMsp: e.target.value }))} />
          </div>
        )}
        {isSuperAdmin && form.isMsp !== 'true' && (
          <>
            <Select label="Client *" options={[{ value: '', label: 'Select Client' }, ...clients.map(c => ({ value: c.id, label: c.name }))]}
              value={form.clientId || ''} onChange={e => { setForm(f => ({ ...f, clientId: e.target.value })); checkSeats(e.target.value); }} />
            {seatWarning && (
              <div style={{ padding: '8px 12px', borderRadius: 8, fontSize: 12, marginBottom: 12, marginTop: -8,
                background: seatWarning.includes('limit reached') ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,66,0.1)',
                color: seatWarning.includes('limit reached') ? '#ef4444' : '#f59e42',
                border: seatWarning.includes('limit reached') ? '1px solid rgba(239,68,68,0.2)' : '1px solid rgba(245,158,66,0.2)' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ verticalAlign: 'middle', marginRight: 6 }}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                {seatWarning}{seatWarning.includes('limit reached') ? ' Contact sales for more seats.' : ''}
              </div>
            )}
          </>
        )}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>
            Groups (Permissions)
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {availableGroups.map(g => {
              const requestorsGroup = availableGroups.find(x => x.name === 'Requestors');
              const requestorsPicked = !!requestorsGroup && form.groupIds?.includes(requestorsGroup.id);
              const otherPicked = (form.groupIds || []).some(id => id !== requestorsGroup?.id);
              const isRequestors = g.name === 'Requestors';
              const MSP_ONLY_GROUPS = ['Super Admins', 'MSP Penetration Testers', 'MSP Billing Admins'];
              const isClientTarget = isClientAdmin || (isSuperAdmin && form.isMsp !== 'true' && !!form.clientId);
              const isMspOnlyGroup = MSP_ONLY_GROUPS.includes(g.name) && isClientTarget;
              const disabled = isMspOnlyGroup || (isRequestors ? otherPicked : requestorsPicked);
              const why = isMspOnlyGroup
                ? `${g.name} is an MSP-only group and cannot be assigned to client users`
                : disabled
                ? (isRequestors
                    ? 'Requestors cannot be combined with another group — it confines the account to the Requests page'
                    : 'A Requestor has access to the Requests page only, so no other group can be added')
                : null;
              return (
              <label key={g.id} title={why || ROLE_SUMMARIES[g.name] || g.description || g.name} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                borderRadius: 6, background: form.groupIds?.includes(g.id) ? 'var(--accent-bg, rgba(59,130,246,0.1))' : 'var(--bg-input)',
                border: form.groupIds?.includes(g.id) ? '1px solid var(--accent, #3b82f6)' : '1px solid var(--border)',
                cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 13, transition: 'all 0.15s',
                opacity: disabled ? 0.4 : 1
              }}>
                <input type="checkbox" checked={form.groupIds?.includes(g.id) || false}
                  disabled={disabled}
                  onChange={e => {
                    setForm(f => ({
                      ...f, groupIds: e.target.checked
                        ? [...(f.groupIds || []), g.id]
                        : (f.groupIds || []).filter(id => id !== g.id)
                    }));
                  }} />
                {g.name}
              </label>
              );
            })}
          </div>
          {(() => {
            const rg = availableGroups.find(x => x.name === 'Requestors');
            return rg && form.groupIds?.includes(rg.id) ? (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>
                A Requestor can submit test requests and follow their progress. No other part of the
                application is available to them, which is why no other group can be added.
              </div>
            ) : null;
          })()}
        </div>
        <Select label="Status" options={[{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }]}
          value={form.isActive ?? 'true'} onChange={e => setForm(f => ({ ...f, isActive: e.target.value }))} />
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button onClick={save} disabled={!editing && (!form.username || !form.email || !form.password || (form.isMsp !== 'true' && !form.clientId))}>
            {editing ? 'Update User' : 'Create User'}
          </Button>
        </div>
      </Modal>

      <Modal isOpen={showResetModal} onClose={() => setShowResetModal(false)}
        title={`Reset Password — ${resetUser?.full_name || resetUser?.username || ''}`} width={440}>
        {resetError && (
          <div style={{ padding: '10px 14px', borderRadius: 8, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', marginBottom: 16, fontSize: 14 }}>
            {resetError}
          </div>
        )}
        {resetSuccess && (
          <div style={{ padding: '10px 14px', borderRadius: 8, background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', marginBottom: 16, fontSize: 14 }}>
            {resetSuccess}
          </div>
        )}
        <Input label="New Password" type="password" value={resetPassword}
          onChange={e => setResetPassword(e.target.value)} />
        <PasswordPolicyHelp password={resetPassword} rules={passwordRules} />
        <Input label="Confirm New Password" type="password" value={resetConfirm}
          onChange={e => setResetConfirm(e.target.value)} />
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
          <Button variant="secondary" onClick={() => setShowResetModal(false)}>Cancel</Button>
          <Button onClick={handleResetPassword} disabled={resetLoading}>
            {resetLoading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </div>
      </Modal>

      {/* Why a user could not be deleted, and what would happen to each record */}
      <Modal isOpen={!!refsModal} onClose={() => setRefsModal(null)}
        title={`Associated records — ${refsModal?.user?.full_name || refsModal?.user?.username || ''}`} width={560}>
        {refsModal && (() => {
          const data = refsModal.data;
          const blocking = (data?.categories || []).filter(c => c.effect === 'blocks');
          const other = (data?.categories || []).filter(c => c.effect !== 'blocks');

          const Row = ({ c }) => (
            <div key={`${c.table}.${c.column}`} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px',
              borderRadius: 6, background: 'var(--bg-input)', border: '1px solid var(--border)',
            }}>
              <span style={{ fontWeight: 600, fontSize: 13, flex: 1 }}>
                {c.label}
                <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--text-muted)' }}> · via {c.via}</span>
              </span>
              <span style={{ fontWeight: 700, fontSize: 13, minWidth: 40, textAlign: 'right' }}>{c.count}</span>
              <span style={{
                fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 8, whiteSpace: 'nowrap',
                background: c.effect === 'blocks' ? 'rgba(239,68,68,0.12)' : 'rgba(107,114,128,0.12)',
                color: c.effect === 'blocks' ? '#ef4444' : 'var(--text-secondary)',
              }}>{c.effectLabel}</span>
            </div>
          );

          return (
            <>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 0, marginBottom: 14 }}>
                {refsModal.message || 'This user cannot be deleted while records reference them.'}
              </p>

              {refsModal.loading ? (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
                  Looking up associated records…
                </div>
              ) : !data || data.categories.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
                  Could not determine which records reference this user.
                </div>
              ) : (
                <>
                  {blocking.length > 0 && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#ef4444', marginBottom: 6 }}>
                        Blocking deletion ({blocking.reduce((n, c) => n + c.count, 0)} record{blocking.reduce((n, c) => n + c.count, 0) === 1 ? '' : 's'})
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {blocking.map(c => <Row key={`${c.table}.${c.column}`} c={c} />)}
                      </div>
                    </div>
                  )}

                  {other.length > 0 && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
                        Would be handled automatically
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {other.map(c => <Row key={`${c.table}.${c.column}`} c={c} />)}
                      </div>
                    </div>
                  )}

                  <div style={{ padding: '10px 14px', borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--border)', fontSize: 12, color: 'var(--text-secondary)' }}>
                    These records are kept deliberately — deleting the user would break the audit
                    trail on work they did. Deactivating blocks sign-in while leaving the history intact.
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                <Button variant="secondary" onClick={() => setRefsModal(null)}>Close</Button>
                {refsModal.user?.is_active !== false && (
                  <Button onClick={() => deactivateUser(refsModal.user)} disabled={deactivating}>
                    {deactivating ? 'Deactivating…' : 'Deactivate instead'}
                  </Button>
                )}
              </div>
            </>
          );
        })()}
      </Modal>
    </div>
  );
}
